<template>
	<div class="content-page content-page">
		<!-- DEV NOTES: the customer nav should be showing with the dynamic customer info pre-populated
		Customer Nav only shows when they access this screen from the customer card on Customers screen.
		-->
		<CustomerNav />
		<div class="content-page__heading" ref="contentPageHeading">
			<div class="content-page__breadcrumb-title">
				<!-- NOTE FOR DEV: Breadcrumb varies depending on how they got to this screen
				If they came from Forms link in header then Added/Edited/Copied a form = < Back to Forms
				If they came from the Customer form manager = Customers > [Customer Name] Forms > Form Designer
				See notes about max chracter limit for customer name
				-->
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">Form Designer</h2>
			</div>

			<div class="content-page__actions">
				<ButtonGroup>
					<template #container>
						<Button
							v-show="showManageCustomers"
							labelText="Manage Clients"
							:btnClick="
								() => {
									displayManageCustomersEditFlyout = true;
								}
							"
						/>

						<Button
							v-show="showPublish"
							enabled
							className="btn--primary btn--has-icon"
							iconClassName="icon-publish"
							labelText="Publish"
							:btnClick="publishForm"
							:iconBeforeLabel="true"
							:disabled="disablePublish"
						/>
					</template>
				</ButtonGroup>
				<div class="pending-changes">
					<span
						class="pending-changes__details"
						v-show="lastPublishedText"
						>{{ lastPublishedText }}
						{{ fullDateString(dataForm.rowModifiedDate) }}</span
					>
					<Button
						v-show="showUndoChanges"
						className="btn--link btn--small"
						labelText="Undo Pending Changes"
						:btnClick="showModalUndoChanges"
					/>
				</div>
			</div>
		</div>
		<!-- DEV NOTES: BlockNotifications component is missing the option to display the status icon based on notification requirement. -->
		<BlockContainer v-if="isSharedForm" ref="blockContainer">
			<BlockNotifications
				title="Shared Form"
				message="This form is shared by multiple clients. Changes will be applied to the form for all assigned clients.
				If changes are unique to one client, create a copy of this form from the Forms Manager for the specific client."
			/>
		</BlockContainer>
		<Spinner :processor="{ loading: loadingSource }" />

		<InfiniteScroll
			ref="contentPageMain"
			:componentData="sendComponentToInfiniteScroll"
			:extraHeightInfinityScroll="extraHeightInfinityScroll"
			v-if="hasFormValue"
			@scrollEnd="handleScollEnd"
			@calculateDivMaxHeight="setCalculateDivMaxHeight"
		>
		</InfiniteScroll>
		<transition name="slide-up">
			<div
				ref="mainContentFooter"
				class="laser-form__footer"
				:class="{ 'is-fixed-footer': displayFooter }"
				v-show="hasFormValue"
			>
				<div class="laser-form__footer-container">
					<div class="laser-form__footer-message">
						<span class="laser-form__footer-message-item">
							{{ changesOnForm }}
						</span>
					</div>
					<div class="laser-form__footer-actions">
						<ButtonGroup>
							<template #container>
								<SplitButton
									variantName="primary"
									:items="splitbuttonItems"
									:value="splitbuttonValue"
									@click="saveForm"
									:disabled="!hasFormChanges"
								/>
								<Button
									className="btn--link"
									labelText="Cancel"
									:btnClick="cancelAction"
								/>
							</template>
						</ButtonGroup>
					</div>
				</div>
			</div>
		</transition>
		<ManageCustomersEditFlyout
			v-model="displayManageCustomersEditFlyout"
			:form="dataForm"
			@toast="updateToast"
		/>
		<Modals
			v-model="showUndoModal"
			type="confirmation"
			:title="'Are you sure you want to undo pending changes?'"
			labelConfirmButton="Yes, undo pending changes"
			labelCancelButton="Cancel"
			@confirm="undoFormChanges"
			@cancel="cancel"
		/>
		<Modals
			v-model="displayConfirmationModal"
			type="confirmation"
			ref="confirmationDialogue"
			:title="'Are you sure you want to leave this page?'"
			:confirmationContent="'Unsaved changes will be lost.'"
		/>
		<FormSaveModal
			v-model="showModal"
			:form="dataForm"
			@show="updateForm"
			@cancel="showModal = false"
		/>
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import BlockContainer from '@/components/notifications/block-notifications/block-notifications-container';
import BlockNotifications from '@/components/notifications/block-notifications/block-notifications';
import SplitButton from '@/components/forms/button/split-button';
import FormSaveModal from '@/views/global-forms/form-save-modal';
import FormDesigner from '@/mixins/forms/form-designer';
import Spinner from '@/components/visual/spinner/spinner';
import ButtonGroup from '@/components/forms/button/button-group';
import ManageCustomersEditFlyout from '@/views/global-forms/manage-customers-edit-flyout.vue';
import Modals from '@/components/shells/modal/modals';
import ScrollMxins from '@/mixins/scroll-mixin';
import InfiniteScroll from '@/components/shells/infinite-scroll/infinite-scroll';
import LaserForm from '@/views/form-designer/laser-form.vue';
import CustomerNav from '@/sections/shared/customer-navegation';
import PageKeyNames from '@/utils/page-key-names';
import { mapState } from 'vuex';
import { DELETE_PDF_FORM } from '@/store/modules/mutation-types';

export default {
	name: 'LaserFormDesigner',
	mixins: [FormDesigner, ScrollMxins],
	components: {
		CustomerNav,
		Button,
		Breadcrumbs,
		BlockContainer,
		BlockNotifications,
		SplitButton,
		FormSaveModal,
		Spinner,
		ButtonGroup,
		Modals,
		ManageCustomersEditFlyout,
		InfiniteScroll
	},
	mounted() {
		this.$store.commit('setCurrentPage', PageKeyNames.FORM);
	},
	destroyed() {
		this.$store.commit('setCurrentPage', null);
	},
	data() {
		return {
			displayManageCustomersEditFlyout: false,
			showUndoModal: false
		};
	},
	computed: {
		...mapState('form', [DELETE_PDF_FORM]),

		sendComponentToInfiniteScroll() {
			return {
				name: 'LaserForm',
				mainPath: '$parent.$refs',
				parentPath: '$parent.$parent.$refs',
				component: {
					is: LaserForm,
					props: [
						{
							form: this.dataForm,
							title: this.titleData
						}
					]
				}
			};
		}
	},
	methods: {
		async deleteFile() {
			if (!this.dataForm.active) {
				this.pdfFileDeleted = true;

				await this.updateForm(true).finally(() => {
					this.dataForm.pdfData = null;
					this.dataForm.laserFormFileName = null;
					this.pdfFileDeleted = false;
				});
			}
		}
	},
	watch: {
		[DELETE_PDF_FORM]: {
			async handler(val) {
				if (val) {
					await this.deleteFile();
					this.$store.commit('form/SetDeletePdfForm', false);
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
  * [Description]
  *
  * Laser Forms designer
  * Can use SCSS variables = TRUE
  *
  *
**/

.content-page {
	@include mq($until: md) {
		margin: 0;
	}

	&__actions {
		display: flex;
		flex-wrap: wrap;
		justify-content: flex-end;

		.pending-changes {
			width: 100%;
			display: flex;
			flex-wrap: wrap;
			justify-content: flex-end;
			margin-top: $gutter-lg;

			@include mq($from: md) {
				display: grid;
				grid-template-columns: auto auto;
				flex-wrap: initial;
				align-items: center;
			}

			&__details {
				text-align: right;
				font-size: 12px;
				color: $neutral-2;
				margin: $gutter-md $gutter-md $gutter-sm 0;
				@include mq($from: md) {
					margin: $gutter-sm $gutter-md $gutter-sm 0;
				}
			}
		}
	}

	.laser-form {
		&__footer {
			margin-top: $gutter-lg;
		}

		&__footer-container {
			display: flex;
			flex-direction: column-reverse;
			flex-wrap: wrap;

			@include mq($from: sm-l) {
				display: grid;
				grid-template-columns: 1fr auto;
				gap: $gutter-md; //matches header
				flex-wrap: wrap;
			}
		}

		&__footer-actions {
			display: inline-flex;
			align-items: center;
			justify-content: flex-end;
		}

		&__footer-message {
			display: inline-flex;
			justify-content: flex-end;
			align-items: center;
			font-size: 13px;

			@include mq($until: sm-l) {
				margin-top: $gutter-lg;
			}
		}
	}
}
</style>
